import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PhoneInput } from '@/components/ui/phone-input'
import { useCommunity } from '@/lib/CommunityContext'
import { countryFromLocale, formatE164 } from '@/lib/phone'
import { cn } from '@/lib/utils'
import { decodeMulti, encodeMulti, minSelectionsOf, multiLabels, safeHref, type FieldWithValue } from './memberFields'

interface MemberFieldsFormProps {
  fields: FieldWithValue[]
  values: Record<string, string>
  onChange: (values: Record<string, string>) => void
  readOnly?: boolean
  disabled?: boolean
  errors?: Record<string, string>
  className?: string
}

/** The custom fields as inputs, one per type; read-only shows the values as text. */
export const MemberFieldsForm = ({ fields, values, onChange, readOnly, disabled, errors = {}, className }: MemberFieldsFormProps) => {
  const { t } = useTranslation('members')
  const { community } = useCommunity()
  const set = (key: string, value: string) => onChange({ ...values, [key]: value })

  /* F-149: "Marque de 1 a 3", "Marque pelo menos 2", "Marque até 3", or nothing. */
  const boundsHint = (field: FieldWithValue) => {
    const min = minSelectionsOf(field)
    const max = field.maxSelections ?? null
    if (min && max) return min === max ? t('fieldsSection.pickExactly', { count: min }) : t('fieldsSection.pickBetween', { min, max })
    if (min > 1) return t('fieldsSection.pickAtLeast', { count: min })
    if (max) return t('fieldsSection.pickAtMost', { count: max })
    return null
  }

  const display = (field: FieldWithValue, value: string) => {
    if (!value) return <span className="text-gray-400">—</span>
    if (field.type === 'MULTI_SELECT') {
      const labels = multiLabels(field.options, value)
      if (!labels.length) return <span className="text-gray-400">—</span>
      return (
        <span className="flex flex-wrap gap-1.5" data-testid="member-field-tags">
          {labels.map((label) => (
            <span key={label} className="rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">{label}</span>
          ))}
        </span>
      )
    }
    if (field.type === 'BOOLEAN') return value === 'true' ? t('fieldsSection.yes') : t('fieldsSection.no')
    if (field.type === 'SELECT') {
      const option = field.options?.find((o) => o.value === value)
      return option ? option.label : t('fieldsSection.removedOption', { value })
    }
    if (field.type === 'DATE') {
      const date = new Date(`${value}T00:00:00`)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString()
    }
    if (field.type === 'PHONE') return formatE164(value)
    if (field.type === 'URL') {
      const href = safeHref(value)
      return href
        ? <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="text-primary underline-offset-2 hover:underline">{value}</a>
        : value
    }
    if (field.type === 'EMAIL') {
      return <a href={`mailto:${value}`} className="text-primary underline-offset-2 hover:underline">{value}</a>
    }
    return value
  }

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', className)} data-testid="member-fields-form">
      {fields.map((field) => {
        const value = values[field.key] ?? ''
        const id = `member-field-${field.key}`
        const error = errors[field.key]
        const multi = field.type === 'MULTI_SELECT'
        const hint = multi && !readOnly ? boundsHint(field) : null
        return (
          <div key={field.key} className={cn('min-w-0 space-y-1', multi && 'sm:col-span-2')}>
            <label htmlFor={multi ? undefined : id} id={multi ? `${id}-label` : undefined} className={cn(readOnly ? 'text-xs text-gray-500' : 'text-sm font-medium', 'block')}>
              {field.label}
              {field.required && !readOnly && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            {readOnly ? (
              <div className="break-words text-sm font-medium text-gray-900">{display(field, value)}</div>
            ) : multi ? (
              <MultiChoice
                id={id}
                options={field.options ?? []}
                selected={decodeMulti(value)}
                max={field.maxSelections ?? null}
                disabled={disabled}
                invalid={!!error}
                onChange={(list) => set(field.key, encodeMulti(list))}
              />
            ) : field.type === 'BOOLEAN' ? (
              <Select value={value || '__empty'} onValueChange={(v) => set(field.key, v === '__empty' ? '' : v)} disabled={disabled}>
                <SelectTrigger id={id} aria-invalid={!!error}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty">{t('fieldsSection.choose')}</SelectItem>
                  <SelectItem value="true">{t('fieldsSection.yes')}</SelectItem>
                  <SelectItem value="false">{t('fieldsSection.no')}</SelectItem>
                </SelectContent>
              </Select>
            ) : field.type === 'SELECT' ? (
              <Select value={value || '__empty'} onValueChange={(v) => set(field.key, v === '__empty' ? '' : v)} disabled={disabled}>
                <SelectTrigger id={id} aria-invalid={!!error}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty">{t('fieldsSection.choose')}</SelectItem>
                  {(field.options ?? []).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                  {value && !field.options?.some((o) => o.value === value) && (
                    <SelectItem value={value}>{t('fieldsSection.removedOption', { value })}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            ) : field.type === 'PHONE' ? (
              <PhoneInput
                id={id}
                value={value}
                defaultCountry={countryFromLocale(community?.language)}
                disabled={disabled}
                className={cn(error && '[&_input]:border-red-400')}
                onChange={(next) => set(field.key, next.phone)}
              />
            ) : (
              <Input
                id={id}
                type={field.type === 'NUMBER' ? 'number' : field.type === 'DATE' ? 'date' : field.type === 'EMAIL' ? 'email' : 'text'}
                inputMode={field.type === 'URL' ? 'url' : undefined}
                placeholder={field.type === 'URL' ? 'https://' : undefined}
                step={field.type === 'NUMBER' ? 'any' : undefined}
                value={value}
                disabled={disabled}
                aria-invalid={!!error}
                className={cn(error && 'border-red-400')}
                onChange={(e) => set(field.key, e.target.value)}
              />
            )}
            {error
              ? <p className="text-xs text-red-600">{error}</p>
              : hint
                ? <p className="text-xs text-gray-500">{hint}</p>
                : !readOnly && field.patternMessage && <p className="text-xs text-gray-500">{field.patternMessage}</p>}
          </div>
        )
      })}
    </div>
  )
}

/**
 * F-149: the options of a multiple choice as boxes to tick, two across from
 * `sm` up. Once the maximum is marked, the unmarked boxes lock, so the limit
 * is felt before the submit rather than read in an error after it.
 */
const MultiChoice = ({ id, options, selected, max, disabled, invalid, onChange }: {
  id: string
  options: { value: string; label: string }[]
  selected: string[]
  max: number | null
  disabled?: boolean
  invalid?: boolean
  onChange: (list: string[]) => void
}) => {
  const own = new Set(selected)
  const full = max !== null && own.size >= max
  /* Kept in the options' order, as the server stores and shows it. */
  const toggle = (value: string) => {
    const next = new Set(own)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange(options.map((o) => o.value).filter((v) => next.has(v)))
  }
  return (
    <div
      role="group"
      aria-labelledby={`${id}-label`}
      aria-invalid={invalid || undefined}
      className={cn('grid gap-1.5 rounded-lg border p-2 sm:grid-cols-2', invalid ? 'border-red-400' : 'border-gray-200')}
      data-testid="member-field-multi"
    >
      {options.map((option) => {
        const checked = own.has(option.value)
        const locked = disabled || (!checked && full)
        return (
          <label
            key={option.value}
            className={cn(
              'flex min-w-0 cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-50',
              checked && 'bg-primary-50/60',
              locked && 'cursor-not-allowed opacity-50 hover:bg-transparent'
            )}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-primary"
              checked={checked}
              disabled={locked}
              onChange={() => toggle(option.value)}
            />
            <span className="min-w-0 break-words">{option.label}</span>
          </label>
        )
      })}
    </div>
  )
}
